/**
 * PasteSmsDialog — quick-entry: paste a bank SMS, parse & post as expense.
 *
 * Reuses the same regex + LLM parser the Gmail pipeline uses (via the new
 * `/api/sms-capture/parse` endpoint) so behaviour stays consistent. Users can
 * preview the extracted fields, pick a card if the last-4 didn't auto-match,
 * and confirm to auto-post — one click, no extra typing.
 */
import { useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquareText, Wand2, CheckCircle2, AlertTriangle } from "lucide-react";

const PLACEHOLDER_SMS = (
    "Rs.1,299.00 spent on HDFC Bank Credit Card XX4977 at STARBUCKS on 08-Jul-26. " +
    "Not you? Call 18002586161."
);

/**
 * Reusable trigger + modal for pasting a bank SMS.
 * Pass `cards` (array of {id, bank_name, last_4}) so the user can pick a card
 * when the parser couldn't auto-match by last-4.
 */
export default function PasteSmsDialog({ cards = [], onPosted, triggerClassName = "", triggerLabel = "Paste bank SMS" }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [sender, setSender] = useState("");
    const [preview, setPreview] = useState(null);
    const [pickedCard, setPickedCard] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setText("");
        setSender("");
        setPreview(null);
        setPickedCard("");
        setBusy(false);
    };

    const parse = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/sms-capture/parse", {
                text: text.trim(),
                sender: sender.trim() || null,
                auto_post: false,
            });
            setPreview(data);
            if (data.preview?.card_id) setPickedCard(data.preview.card_id);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const confirmAndPost = async () => {
        if (!preview) return;
        const cardId = pickedCard || preview.preview.card_id;
        if (!cardId) {
            toast.error("Pick a card before posting.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/sms-capture/parse", {
                text: text.trim(),
                sender: sender.trim() || null,
                auto_post: true,
            });
            if (data.outcome === "posted") {
                toast.success("Expense posted from SMS.");
                onPosted?.(data.preview);
                reset();
                setOpen(false);
            } else if (data.outcome === "duplicate") {
                toast.info("This transaction is already recorded.");
            } else {
                toast.warning("Could not auto-post — check the preview above.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const p = preview?.preview;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-stone-200 bg-white text-xs font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors duration-200 ${triggerClassName}`}
                data-testid="paste-sms-trigger"
            >
                <MessageSquareText size={13} /> {triggerLabel}
            </button>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                <DialogContent className="max-w-lg" data-testid="paste-sms-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Paste a bank SMS</DialogTitle>
                    </DialogHeader>

                    {!preview && (
                        <div className="space-y-3">
                            <div>
                                <Label className="eyebrow">SMS text</Label>
                                <Textarea
                                    rows={5}
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={PLACEHOLDER_SMS}
                                    className="mt-1.5 font-mono text-xs"
                                    data-testid="paste-sms-text"
                                />
                                <div className="text-[11px] text-stone-500 mt-1.5">
                                    Anything the bank sent — we&rsquo;ll extract amount, card, merchant, and date.
                                </div>
                            </div>
                            <div>
                                <Label className="eyebrow">Sender ID (optional)</Label>
                                <Input
                                    value={sender}
                                    onChange={(e) => setSender(e.target.value)}
                                    placeholder="e.g. AD-HDFCBK, VM-ICICIB"
                                    className="mt-1.5"
                                />
                                <div className="text-[11px] text-stone-500 mt-1.5">
                                    Helps identify the bank when the message body doesn&rsquo;t say it.
                                </div>
                            </div>
                        </div>
                    )}

                    {preview && p && (
                        <div className="space-y-3" data-testid="paste-sms-preview">
                            {preview.outcome === "duplicate" ? (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                                    <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
                                    <div className="text-sm text-amber-900">
                                        Already recorded. If you think this is a different swipe, use{" "}
                                        <span className="underline">Add expense</span> directly.
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-start gap-2">
                                    <CheckCircle2 size={14} className="text-emerald-700 shrink-0 mt-0.5" />
                                    <div className="text-sm text-emerald-900">
                                        Parsed. Confidence <strong>{p.confidence}/4</strong>.{" "}
                                        {!p.card_id && "Pick a card below before posting."}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="eyebrow">Amount</div>
                                    <div className="mt-1 tabular-nums font-medium">{p.amount ? fmtMoney(p.amount) : "—"}</div>
                                </div>
                                <div>
                                    <div className="eyebrow">Merchant</div>
                                    <div className="mt-1">{p.merchant || <span className="text-stone-400">—</span>}</div>
                                </div>
                                <div>
                                    <div className="eyebrow">Last 4</div>
                                    <div className="mt-1 font-mono-figure">{p.last_4 || <span className="text-stone-400">—</span>}</div>
                                </div>
                                <div>
                                    <div className="eyebrow">Category</div>
                                    <div className="mt-1">{p.category} <span className="text-[11px] text-stone-500">· {p.rule_source}</span></div>
                                </div>
                            </div>

                            <div>
                                <Label className="eyebrow">Card</Label>
                                <Select value={pickedCard} onValueChange={setPickedCard}>
                                    <SelectTrigger className="mt-1.5" data-testid="paste-sms-card-picker">
                                        <SelectValue placeholder="Pick a card…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cards.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.bank_name} · {c.card_name || "Card"} · •••• {c.last_4}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {p.bank_hint && !p.card_id && (
                                    <div className="text-[11px] text-stone-500 mt-1.5">
                                        Detected bank hint: <strong>{p.bank_hint}</strong> — pick the exact card.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {!preview ? (
                            <>
                                <Button variant="ghost" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                                <Button
                                    onClick={parse}
                                    disabled={busy || text.trim().length < 8}
                                    data-testid="paste-sms-parse-btn"
                                >
                                    <Wand2 size={14} className="mr-1.5" />
                                    {busy ? "Parsing…" : "Parse SMS"}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="ghost" onClick={() => setPreview(null)}>← Edit paste</Button>
                                <Button
                                    onClick={confirmAndPost}
                                    disabled={busy || preview.outcome === "duplicate" || !(pickedCard || p?.card_id)}
                                    data-testid="paste-sms-post-btn"
                                >
                                    {busy ? "Posting…" : "Confirm & post expense"}
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
